import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ArrowPathIcon, ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import HelpButton from "../../../components/Help/HelpButton";
import ReviewGuide from "../../../components/Help/guides/ReviewGuide";
import OverflowMenu from "../../../components/ui/OverflowMenu";
import { Button } from "../../../components/forms/Buttons/Button";
import { getCodedErrorMessage } from "../../../utils/apiError";
import { UserContext } from "../../../utils/contexts/UserContext";
import { cardClass, focusRing } from "../../Exams/TestBuilder/ui";
import QuestionEditButton, { questionEditPath } from "../QuestionEditButton";
import DeleteQuestionDialog, { canDeleteQuestion } from "./DeleteQuestionDialog";
import { leavePage } from "./reviewUtils";

// Where Back (and a deleted question's page) goes when the page was opened from outside the app (a new tab, a pasted
// link): its subject in the bank, or the bank.
const bankPath = (question) => (question?.folder?.uuid ? `/questions?folder=${question.folder.uuid}` : "/questions");

// Quiet (no frame): the page's main button is Edit. A 44px arrow on phones, with its label from md up.
const backButton = `inline-flex h-11 w-11 shrink-0 items-center justify-center gap-1.5 rounded-lg text-sm font-medium text-slate-600 transition duration-200 hover:bg-slate-100 hover:text-slate-900 md:h-9 md:w-auto md:px-2.5 ${focusRing}`;

/**
 * The question page's top bar, for both of its tabs: Back, the Question | Versions tabs (`tabs`), the guide's "?",
 * then what the user may do with the question itself (with `actions`, the question tab): Edit, its one main button,
 * and the delete in "⋯" (confirmed in its dialog). From md up one row (wrapping where it must); on phones Back, the
 * tabs and "?" make the first row and Edit fills the second next to "⋯". `onReload` reads the question again when a
 * delete finds the page out of date (the dialog stays mounted meanwhile, so it closes smoothly even when the delete
 * is gone after the reload). Without a question (loading, or failed) only Back.
 */
export default function QuestionTopBar({ question, folderRole = null, onReload, tabs = null, actions = true, className = "" }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useContext(UserContext) || {};
  const [deleteOpen, setDeleteOpen] = useState(false);
  const editable = actions && !!questionEditPath(question, user, folderRole);
  const deletable = actions && canDeleteQuestion(question);

  return (
    <div className={`flex flex-wrap items-center gap-x-2 gap-y-3 ${className}`}>
      <div className="flex min-w-0 items-center gap-2">
        <button type="button" onClick={() => leavePage(navigate, bankPath(question))} className={backButton}>
          <ArrowLeftIcon className="h-5 w-5 shrink-0 md:h-4 md:w-4" aria-hidden="true" />
          <span className="max-md:sr-only">{t("buttons.back")}</span>
        </button>
        {tabs}
        {question && (
          <HelpButton id="review" title={t("help.review.title")}>
            <ReviewGuide />
          </HelpButton>
        )}
      </div>
      {(editable || deletable) && (
        <div className="flex items-center gap-2 max-md:w-full md:ml-auto">
          {editable && <QuestionEditButton question={question} folderRole={folderRole} className="max-md:flex-1" />}
          {deletable && (
            <OverflowMenu
              label={t("question_detail.actions_menu")}
              testId="question-actions"
              items={[{ key: "delete", label: t("buttons.delete"), Icon: TrashIcon, danger: true, onClick: () => setDeleteOpen(true) }]}
            />
          )}
        </div>
      )}
      {question && actions && (
        <DeleteQuestionDialog
          question={question}
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => leavePage(navigate, bankPath(question))}
          onReload={onReload}
        />
      )}
    </div>
  );
}

// The page while its question loads, or why it could not be read (gone, or not given to the user). `onRetry` reads
// it again after a failure that may pass (no answer, or the server's own).
export function QuestionPageState({ error, onRetry }) {
  const { t } = useTranslation();
  if (!error) return <p className="p-6 text-sm text-slate-500">{t("question_detail.loading")}</p>;
  const status = error?.response?.status;
  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 motion-safe:animate-fade-in">
      <QuestionTopBar question={null} />
      <div className={`${cardClass} flex flex-wrap items-center gap-3 p-5 sm:p-6`} role="alert">
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
        {/* On a phone the retry goes under the text rather than squeezing it. */}
        <p className="min-w-[12rem] flex-1 text-sm text-slate-700">
          {status === 404
            ? t("question_detail.review.not_found")
            : getCodedErrorMessage(t, error, t("question_detail.review.load_failed"))}
        </p>
        {onRetry && (!status || status >= 500) && (
          <Button
            text={t("question_detail.review.retry")}
            Icon={ArrowPathIcon}
            color="secondary-outline"
            onClick={onRetry}
            className="!h-9 !px-3 max-md:!h-11"
          />
        )}
      </div>
    </div>
  );
}
